/**
 * The poll compose sheet (R5), opened from the composer's poll action:
 * `New poll`, a question field, two or more option fields with add/remove
 * controls, a "Let people pick more than one" checkbox off by default, and
 * a `Post poll` primary disabled until a real question and at least two
 * non-empty options exist. Uses `Checkbox`, not `Switch`: this is a form
 * value submitted together at `Post poll`, not a setting that writes the
 * moment it's flipped (`Switch.tsx`'s own header comment draws exactly this
 * line).
 */
import { Button, Checkbox, Dialog, Input } from "@sportsmet/ui";
import type { ReactNode } from "react";
import { useState } from "react";

import {
  POLL_ADD_OPTION_LABEL,
  POLL_ALLOW_MULTIPLE_LABEL,
  POLL_COMPOSE_TITLE,
  POLL_OPTION_PLACEHOLDER,
  POLL_POST_LABEL,
  POLL_QUESTION_LABEL,
  POLL_QUESTION_PLACEHOLDER,
  POLL_REMOVE_OPTION_LABEL,
} from "./chatExtrasCopy";
import {
  addPollOption,
  buildSendablePollDraft,
  isPollDraftValid,
  POLL_MIN_OPTIONS,
  removePollOption,
  type typePollDraft,
  updatePollOption,
} from "./pollComposeLogic";

export type typePollComposeSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (draft: typePollDraft) => void;
  isSubmitting: boolean;
};

const BLANK_DRAFT: typePollDraft = {
  question: "",
  allowMultiple: false,
  optionLabels: ["", ""],
};

export const PollComposeSheet = (
  props: typePollComposeSheetProps,
): ReactNode => {
  const [draft, setDraft] = useState<typePollDraft>(BLANK_DRAFT);

  const handleOpenChange = (next: boolean): void => {
    if (!next) setDraft(BLANK_DRAFT);
    props.onOpenChange(next);
  };

  const handleSubmit = (): void => {
    if (!isPollDraftValid(draft)) return;
    props.onSubmit(buildSendablePollDraft(draft));
    setDraft(BLANK_DRAFT);
    props.onOpenChange(false);
  };

  const canRemove = draft.optionLabels.length > POLL_MIN_OPTIONS;

  return (
    <Dialog
      open={props.open}
      onOpenChange={handleOpenChange}
      title={POLL_COMPOSE_TITLE}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="poll-post-submit"
            disabled={!isPollDraftValid(draft) || props.isSubmitting}
            loading={props.isSubmitting}
            onClick={handleSubmit}
          >
            {POLL_POST_LABEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label={POLL_QUESTION_LABEL}
          placeholder={POLL_QUESTION_PLACEHOLDER}
          required
          data-testid="poll-question-input"
          value={draft.question}
          onChange={(e) => {
            setDraft((prev) => ({ ...prev, question: e.target.value }));
          }}
        />

        <div className="flex flex-col gap-2">
          {draft.optionLabels.map((label, index) => (
            <div key={index} className="flex items-end gap-2">
              <Input
                label={`${POLL_OPTION_PLACEHOLDER} ${index + 1}`}
                placeholder={POLL_OPTION_PLACEHOLDER}
                data-testid={`poll-option-input-${index}`}
                value={label}
                onChange={(e) => {
                  setDraft((prev) => ({
                    ...prev,
                    optionLabels: updatePollOption(
                      prev.optionLabels,
                      index,
                      e.target.value,
                    ),
                  }));
                }}
              />
              {canRemove ? (
                <button
                  type="button"
                  aria-label={POLL_REMOVE_OPTION_LABEL}
                  data-testid={`poll-option-remove-${index}`}
                  onClick={() => {
                    setDraft((prev) => ({
                      ...prev,
                      optionLabels: removePollOption(prev.optionLabels, index),
                    }));
                  }}
                  className="min-h-touch-target min-w-touch-target text-body text-ink-tertiary"
                >
                  ✕
                </button>
              ) : null}
            </div>
          ))}
          <button
            type="button"
            data-testid="poll-add-option"
            onClick={() => {
              setDraft((prev) => ({
                ...prev,
                optionLabels: addPollOption(prev.optionLabels),
              }));
            }}
            className="self-start text-caption text-primary"
          >
            {POLL_ADD_OPTION_LABEL}
          </button>
        </div>

        <Checkbox
          label={POLL_ALLOW_MULTIPLE_LABEL}
          checked={draft.allowMultiple}
          onCheckedChange={(checked) => {
            setDraft((prev) => ({
              ...prev,
              allowMultiple: checked === true,
            }));
          }}
        />
      </div>
    </Dialog>
  );
};
